import type { LevelMeta } from './levels'
import type { StarCount, TakeMeHomeSave } from '@/types/save'

export interface RunOutcome {
  reachedHome: boolean
  slides: number
  coinsCollected: number
  timeMs: number
}

const COIN_VALUE = 10
const ROUTE_BONUS = 500
const STAR_BONUS = 250

export const LEVEL_ADVANCE_COUNTDOWN = [3, 2, 1] as const

export interface ScoreBreakdown {
  coins: number
  timeBonus: number
  routeBonus: number
  starBonus: number
  total: number
}

export function computeStars(level: LevelMeta, run: RunOutcome): StarCount {
  if (!run.reachedHome) return 0
  let stars = 1
  if (run.slides <= level.stepTarget) stars += 1
  const totalCollectibles = level.coinCount + level.gemCount
  const allCoins = totalCollectibles === 0 || run.coinsCollected >= totalCollectibles
  const underTime = run.timeMs <= level.timeTargetMs
  if (allCoins || underTime) stars += 1
  return Math.min(3, stars) as StarCount
}

export function computeScore(level: LevelMeta, run: RunOutcome, stars: StarCount): number {
  if (!run.reachedHome) return 0
  return computeScoreBreakdown(level, run, stars).total
}

export function computeScoreBreakdown(level: LevelMeta, run: RunOutcome, stars: StarCount): ScoreBreakdown {
  if (!run.reachedHome) {
    return { coins: 0, timeBonus: 0, routeBonus: 0, starBonus: 0, total: 0 }
  }
  const coinScore = run.coinsCollected * COIN_VALUE
  const timeBonus = Math.max(0, Math.round((level.timeTargetMs - run.timeMs) / 100))
  const routeBonus = run.slides <= level.stepTarget ? ROUTE_BONUS : 0
  const starBonus = stars * STAR_BONUS
  return {
    coins: coinScore,
    timeBonus,
    routeBonus,
    starBonus,
    total: coinScore + timeBonus + routeBonus + starBonus,
  }
}

export function nextLevelAdvanceCountdownStep(current: number | null): number | null {
  if (current == null) return LEVEL_ADVANCE_COUNTDOWN[0]
  const index = LEVEL_ADVANCE_COUNTDOWN.indexOf(current as (typeof LEVEL_ADVANCE_COUNTDOWN)[number])
  return index >= 0 ? (LEVEL_ADVANCE_COUNTDOWN[index + 1] ?? null) : null
}

export function computeGraceScore(save: TakeMeHomeSave): number {
  const starSum = Object.values(save.progress.levelStars).reduce((a: number, b) => a + b, 0)
  const perfectRoutes = Object.keys(save.progress.levelBest).filter((k) => {
    const idx = Number(k)
    const best = save.progress.levelBest[idx]
    const level = save.progress.levelStars[idx]
    return best && level === 3
  }).length
  return starSum * 1000 + save.meta.totalScore + perfectRoutes * 500
}

export interface GraceMeta {
  totalStars: number
  biomesCleared: number
  perfectRoutes: number
  topBiome: string
}
